import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import classes from "./Navigation.module.css";
import {
  getSecretAccess,
  grantSecretAccess,
  matchesSecretCode,
  revokeSecretAccess,
} from "../utils/secretGate";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const isSecretRoute = location.pathname === "/secret";
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [code, setCode] = useState("");
  const [warning, setWarning] = useState("");
  const [hasSecretAccess, setHasSecretAccess] = useState(() => getSecretAccess());
  const activeIndex = isSecretRoute || isUnlocking ? 1 : 0;

  useEffect(() => {
    setHasSecretAccess(getSecretAccess());
  }, [location.pathname]);

  useEffect(() => {
    if (isUnlocking && location.pathname === "/") {
      document.body.dataset.secretUnlocking = "true";
    } else {
      delete document.body.dataset.secretUnlocking;
    }

    return () => {
      delete document.body.dataset.secretUnlocking;
    };
  }, [isUnlocking, location.pathname]);

  useEffect(() => {
    if (!isUnlocking) {
      return undefined;
    }

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return undefined;
  }, [isUnlocking]);

  const closeUnlock = () => {
    setIsUnlocking(false);
    setCode("");
    setWarning("");
  };

  const openUnlock = () => {
    if (isSecretRoute) {
      return;
    }

    setIsUnlocking(true);
    setWarning("");
  };

  const handleHomeClick = () => {
    if (isUnlocking) {
      closeUnlock();
      return;
    }

    if (isSecretRoute) {
      revokeSecretAccess();
      setHasSecretAccess(false);
    }
  };

  const handleSecretSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }

    if (matchesSecretCode(code)) {
      grantSecretAccess();
      setHasSecretAccess(true);
      closeUnlock();
      navigate("/secret");
      return;
    }

    setWarning("Try again.");
  };

  return (
    <header className={classes.header}>
      <div
        className={classes.shell}
        data-unlocking={isUnlocking ? "true" : "false"}
      >
        <div className={classes.surface}></div>
        <ul className={classes.list} style={{ "--active-index": activeIndex }}>
          <span className={classes.activePill} aria-hidden="true"></span>
          <li>
            <NavLink
              to="/"
              onClick={handleHomeClick}
              className={({ isActive }) =>
                `${classes.link} ${isActive ? classes.active : ""}`.trim()
              }
            >
              Achen
            </NavLink>
          </li>
          <li>
            <button
              type="button"
              onClick={openUnlock}
              className={`${classes.link} ${
                isSecretRoute && hasSecretAccess ? classes.active : ""
              }`.trim()}
              aria-expanded={isUnlocking}
              aria-controls="secret-unlock"
            >
              Secret
            </button>
          </li>
        </ul>

        <div className={classes.islandSlot} aria-hidden={!isUnlocking}>
          <div
            id="secret-unlock"
            className={classes.unlockForm}
            role="group"
            aria-label="Secret code entry"
          >
            <label className={classes.unlockLabel} htmlFor="secret-code">
              Type the code you see
            </label>
            <input
              id="secret-code"
              ref={inputRef}
              className={classes.unlockInput}
              type="text"
              name="secret_access_key"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                if (warning) {
                  setWarning("");
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSecretSubmit(event);
                }
              }}
              placeholder="Hello"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck="false"
              inputMode="text"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
            />
            <div className={classes.unlockActions}>
              <button
                type="button"
                className={classes.confirmButton}
                onClick={handleSecretSubmit}
              >
                Enter
              </button>
              <button
                type="button"
                className={classes.cancelButton}
                onClick={closeUnlock}
              >
                Cancel
              </button>
            </div>
            <p
              className={classes.warning}
              role="status"
              aria-live="polite"
            >
              {warning}
            </p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
